import { lazy, Suspense } from 'react';
import { PlanGuard } from '../components/PlanGuard';
import { RouteObject } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';

// Lazy loaded components
const HomePage = lazy(() => import('../pages/home/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const RegisterPage = lazy(() => import('../pages/register/page'));
const ConfirmarCuentaPage = lazy(() => import('../pages/confirmar-cuenta/page'));
const RecuperarContrasenaPage = lazy(() => import('../pages/recuperar-contrasena/page'));
const RestablecerContrasenaPage = lazy(() => import('../pages/restablecer-contrasena/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const KidsDashboardPage = lazy(() => import('../pages/kids-dashboard/page'));
const AventurasPage = lazy(() => import('../pages/aventuras/page'));
const DemoPage = lazy(() => import('../pages/demo/page'));
const TutoriaPage = lazy(() => import('../pages/tutoria/page'));
const EstudiantePage = lazy(() => import('../pages/estudiante/page'));
const TareasPage = lazy(() => import('../pages/tareas/page'));
const CalendarioPage = lazy(() => import('../pages/calendario/page'));
const ChatPage = lazy(() => import('../pages/chat/page'));
const ReportesPage = lazy(() => import('../pages/reportes/page'));
const SimulacrosPage = lazy(() => import('../pages/simulacros/page'));
const SmartStudyPlanPage = lazy(() => import('../pages/smart-study-plan/page'));
const LogrosPage = lazy(() => import('../pages/logros/page'));
const PerfilPage = lazy(() => import('../pages/perfil/page'));
const PlanesPage = lazy(() => import('../pages/planes/page'));
const JuegosPage = lazy(() => import('../pages/juegos/page'));
const MaquinaTiempoPage = lazy(() => import('../pages/juegos/maquina-tiempo/page'));
const MaquinaTiempoChatPage = lazy(() => import('../pages/juegos/maquina-tiempo/chat/page'));
const PalabrasSecretasPage = lazy(() => import('../pages/juegos/palabras-secretas/page'));
const PalabrasSecretasJugarPage = lazy(() => import('../pages/juegos/palabras-secretas/jugar/page'));
const GeoMysteryPage = lazy(() => import('../pages/juegos/geo-mystery/page'));
const GeoMysteryJugarPage = lazy(() => import('../pages/juegos/geo-mystery/jugar/page'));
const DrIAPage = lazy(() => import('../pages/juegos/dr-ia/page'));
const DiagnosticarPage = lazy(() => import('../pages/juegos/dr-ia/diagnosticar/page'));
const ResultadosPage = lazy(() => import('../pages/juegos/dr-ia/resultados/page'));
const MatematicasPage = lazy(() => import('../pages/juegos/matematicas/page'));
const DesafioRapidoPage = lazy(() => import('../pages/juegos/matematicas/desafio-rapido/page'));
const AventuraMatematicaPage = lazy(() => import('../pages/juegos/matematicas/aventura/page'));
const DueloNumerosPage = lazy(() => import('../pages/juegos/matematicas/duelo-numeros/page'));
const MemoryMatchPage = lazy(() => import('../pages/juegos/matematicas/memory-match/page'));
const ZoologicoEmojisPage = lazy(() => import('../pages/juegos/zoologico-emojis/page'));
const ZoologicoEmojisJugarPage = lazy(() => import('../pages/juegos/zoologico-emojis/jugar/page'));
const SonidosLetrasPage = lazy(() => import('../pages/juegos/sonidos-letras/page'));
const SonidosLetrasJugarPage = lazy(() => import('../pages/juegos/sonidos-letras/jugar/page'));
const ContandoDivertidoPage = lazy(() => import('../pages/juegos/contando-divertido/page'));
const ContandoDivertidoJugarPage = lazy(() => import('../pages/juegos/contando-divertido/jugar/page'));
const SeleccionarAvatarPage = lazy(() => import('../pages/seleccionar-avatar/page'));
const PrivacidadPage = lazy(() => import('../pages/privacidad/page'));
const TerminosPage = lazy(() => import('../pages/terminos/page'));
const AccesibilidadPage = lazy(() => import('../pages/accesibilidad/page'));
const GuiasPadresPage = lazy(() => import('../pages/guias-padres/page'));
const MascotaPage = lazy(() => import('../pages/mascota/page'));
const FAQPage = lazy(() => import('../pages/faq/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const BlogIndexPage = lazy(() => import('../pages/blog/page'));
const BlogArticlePage = lazy(() => import('../pages/blog/article/page'));
const AgentSelectionPage = lazy(() => import('../pages/AgentSelection'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const LiveSessionPage = lazy(() => import('../pages/live-session/LiveSession').then(module => ({ default: module.LiveSession })));

// Wrapper component for lazy loading with error boundary
const LazyWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <LazyWrapper>
        <HomePage />
      </LazyWrapper>
    ),
  },
  {
    path: '/login',
    element: (
      <LazyWrapper>
        <LoginPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/iniciar-sesion',
    element: (
      <LazyWrapper>
        <LoginPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/register',
    element: (
      <LazyWrapper>
        <RegisterPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/registro',
    element: (
      <LazyWrapper>
        <RegisterPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/confirmar-cuenta',
    element: (
      <LazyWrapper>
        <ConfirmarCuentaPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/recuperar-contrasena',
    element: (
      <LazyWrapper>
        <RecuperarContrasenaPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/restablecer-contrasena',
    element: (
      <LazyWrapper>
        <RestablecerContrasenaPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <LazyWrapper>
        <DashboardPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/kids-dashboard',
    element: (
      <LazyWrapper>
        <KidsDashboardPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/aventuras',
    element: (
      <LazyWrapper>
        <AventurasPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/seleccionar-avatar',
    element: (
      <LazyWrapper>
        <SeleccionarAvatarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/demo',
    element: (
      <LazyWrapper>
        <DemoPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/tutoria',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="aiTutoring">
          <TutoriaPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/tareas',
    element: (
      <LazyWrapper>
        <TareasPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/estudiante',
    element: (
      <LazyWrapper>
        <EstudiantePage />
      </LazyWrapper>
    ),
  },
  {
    path: '/calendario',
    element: (
      <LazyWrapper>
        <CalendarioPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/chat',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="aiTutoring">
          <ChatPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/agents',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="aiTutoring">
          <AgentSelectionPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/reportes',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="aiTutoring">
          <ReportesPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/simulacros',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="practiceTests">
          <SimulacrosPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/smart-study-plan',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="smartStudyPlan">
          <SmartStudyPlanPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '/planes',
    element: (
      <LazyWrapper>
        <PlanesPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/perfil',
    element: (
      <LazyWrapper>
        <PerfilPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos',
    element: (
      <LazyWrapper>
        <JuegosPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/maquina-tiempo',
    element: (
      <LazyWrapper>
        <MaquinaTiempoPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/maquina-tiempo/chat',
    element: (
      <LazyWrapper>
        <MaquinaTiempoChatPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/palabras-secretas',
    element: (
      <LazyWrapper>
        <PalabrasSecretasPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/palabras-secretas/jugar',
    element: (
      <LazyWrapper>
        <PalabrasSecretasJugarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/geo-mystery',
    element: (
      <LazyWrapper>
        <GeoMysteryPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/geo-mystery/jugar',
    element: (
      <LazyWrapper>
        <GeoMysteryJugarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/dr-ia',
    element: (
      <LazyWrapper>
        <DrIAPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/dr-ia/diagnosticar',
    element: (
      <LazyWrapper>
        <DiagnosticarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/dr-ia/resultados',
    element: (
      <LazyWrapper>
        <ResultadosPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/matematicas',
    element: (
      <LazyWrapper>
        <MatematicasPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/matematicas/desafio-rapido',
    element: (
      <LazyWrapper>
        <DesafioRapidoPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/matematicas/aventura',
    element: (
      <LazyWrapper>
        <AventuraMatematicaPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/matematicas/duelo-numeros',
    element: (
      <LazyWrapper>
        <DueloNumerosPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/matematicas/memory-match',
    element: (
      <LazyWrapper>
        <MemoryMatchPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/zoologico-emojis',
    element: (
      <LazyWrapper>
        <ZoologicoEmojisPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/zoologico-emojis/jugar',
    element: (
      <LazyWrapper>
        <ZoologicoEmojisJugarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/sonidos-letras',
    element: (
      <LazyWrapper>
        <SonidosLetrasPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/sonidos-letras/jugar',
    element: (
      <LazyWrapper>
        <SonidosLetrasJugarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/contando-divertido',
    element: (
      <LazyWrapper>
        <ContandoDivertidoPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/juegos/contando-divertido/jugar',
    element: (
      <LazyWrapper>
        <ContandoDivertidoJugarPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/logros',
    element: (
      <LazyWrapper>
        <LogrosPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/privacidad',
    element: <PrivacidadPage />,
  },
  {
    path: '/privacy',
    element: <PrivacidadPage />,
  },
  {
    path: '/terminos',
    element: <TerminosPage />,
  },
  {
    path: '/terms',
    element: <TerminosPage />,
  },
  {
    path: '/accesibilidad',
    element: <AccesibilidadPage />,
  },
  {
    path: '/accessibility',
    element: <AccesibilidadPage />,
  },
  {
    path: '/guias-padres',
    element: (
      <LazyWrapper>
        <GuiasPadresPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/parent-guides',
    element: (
      <LazyWrapper>
        <GuiasPadresPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/mascota',
    element: (
      <LazyWrapper>
        <MascotaPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/faq',
    element: (
      <LazyWrapper>
        <FAQPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/about',
    element: (
      <LazyWrapper>
        <AboutPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/sobre-nosotros',
    element: (
      <LazyWrapper>
        <AboutPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/blog',
    element: (
      <LazyWrapper>
        <BlogIndexPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/blog/:slug',
    element: (
      <LazyWrapper>
        <BlogArticlePage />
      </LazyWrapper>
    ),
  },
  {
    path: '/live-session',
    element: (
      <LazyWrapper>
        <PlanGuard requiredFeature="liveSessions">
          <LiveSessionPage />
        </PlanGuard>
      </LazyWrapper>
    ),
  },
  {
    path: '*',
    element: (
      <LazyWrapper>
        <NotFoundPage />
      </LazyWrapper>
    ),
  },
];

export default routes;
